import * as THREE from 'three';
import { loadTexture } from './textureLoad';

// Textures for authored rooms, applied at RUNTIME by material name.
//
// ── WHY NOT BAKED INTO THE GLB ───────────────────────────────────────────────
// Because a texture is the part most likely to change and the part most
// expensive to ship. Baking would put every map inside the model, so re-skinning
// a floor means regenerating and re-downloading the whole room, and a district
// that wants the same room in a different material needs a second copy of it.
//
// Keeping them out means the salon GLB stays 2.2MB of pure geometry, the maps
// are separately cacheable, and a room can be re-skinned by editing this file.
// It is also the seam where per-district material sets go later: the same
// geometry, a different table.
//
// ── WHAT MAKES IT WORK ───────────────────────────────────────────────────────
// The kit names materials semantically — `floor`, `wall`, `walnut`, `stone` —
// so this table joins on meaning rather than on whatever a modelling package
// called something. And the geometry already carries WORLD-SCALE UVs (kit.mjs
// uvProject), so `repeat` is not a number anyone tunes: the UVs are in metres
// and the map lands at its real size on the floor, a counter or a wall alike.
//
// The palette colour stays underneath as a tint, which is also the fallback —
// a map that fails to load leaves the untextured room rather than a black one.
// ── WHY EACH MATERIAL GETS THREE MAPS, NOT ONE ───────────────────────────────
// Colour alone cannot make a surface look like a material. An albedo-only floor
// is a PICTURE of terrazzo lying on the ground — right colours in the right
// places, returning light like paper, identically in every direction, so it
// stays flat as you walk past it. What sells stone as stone is that its polish
// varies (roughness) and its chips sit a hair proud of the matrix (normal).
// Those two are what make a highlight TRAVEL across a surface as you move, and
// that motion is most of what "photographic" means in a real-time scene.
//
// Both are derived from the albedo at build time rather than generated — see
// scripts/make_texture.mjs in the API repo for why that is more reliable than
// asking a model for them, not just cheaper.
//
// `normalScale` is the one number worth tuning per material: the derivation
// assumes dark = recessed, which is true of grain, grout and plaster tooth and
// false of anything where colour and depth are unrelated. Keep it low enough
// that a stripe never reads as a groove.
const SETS = {
  salon: {
    floor:  { map: 'salon_floor',   normalScale: 0.5 },
    // THE CREAM WALLS STAY UNTEXTURED, and the reason is worth keeping.
    //
    // salon_plaster is on disk and works — as an image. On the wall it printed a
    // repeating bowtie motif, one per tile, because the trowel sweeps it needed
    // in order to have any surface relief were exactly the directional features
    // that mirror-tiling folds into a symmetric shape. The two requirements
    // fight each other: enough sweep to catch light, little enough to survive
    // mirroring. Plaster lost.
    //
    // Left unwired rather than deleted (public/textures/salon_plaster*). To try
    // again, the version that would work is one with NO large features at all —
    // fine uniform tooth only, high frequency, no sweeps — which mirrors
    // invisibly and seams below the noise floor. Flat colour beats a motif in
    // the meantime, and the walls read fine against a textured floor.
    accent: { map: 'salon_accent',  normalScale: 0.4 },
    walnut: { map: 'salon_wood',    normalScale: 0.7 },
    stone:  { map: 'salon_stone',   normalScale: 0.25 },
  },
};

export function texturesFor(roomId) { return SETS[roomId] || null; }

// Apply a room's set to a loaded model. Returns how many materials were skinned.
export function applyRoomTextures(model, roomId, onDone) {
  const set = texturesFor(roomId);
  if (!model || !set) return 0;

  const seen = new Map();
  model.traverse((o) => {
    if (!o.isMesh || !o.material) return;
    const spec = set[o.material.name];
    if (!spec) return;
    // CLONE BEFORE SKINNING. useGLTF caches the parsed scene and clone(true)
    // SHARES materials with it, so assigning a map here would mutate the cached
    // original — and the next room built on the same GLB would inherit it.
    if (!seen.has(o.material.name)) {
      const mat = o.material.clone();
      mat.name = o.material.name;
      if (spec.roughness != null) mat.roughness = spec.roughness;
      if (spec.metalness != null) mat.metalness = spec.metalness;
      seen.set(o.material.name, { mat, spec });
    }
    o.material = seen.get(o.material.name).mat;
  });

  let pending = 0;
  const settle = () => { pending -= 1; if (pending <= 0) onDone?.(); };

  // COLOUR SPACE IS NOT A DETAIL HERE. The albedo is a picture and has to be
  // decoded from sRGB. The normal and roughness maps are DATA — vectors and
  // scalars that happen to be stored in an image — and decoding them as sRGB
  // bends every value through a gamma curve, which tilts every normal and skews
  // every roughness. It does not error; it just quietly looks wrong.
  const load = (file, srgb) => loadTexture(`/textures/${file}.jpg`).then((tex) => {
    if (!tex) return null;
    // REPEAT, because the UVs run in metres and go far past 1. Without it three
    // clamps to the edge and the whole floor becomes one stretched border pixel
    // — which looks exactly like "the texture failed to load".
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.colorSpace = srgb ? THREE.SRGBColorSpace : THREE.NoColorSpace;
    // Floors and worktops are seen at grazing angles, where without this the
    // texture mushes into a grey smear a few metres out.
    tex.anisotropy = 8;
    return tex;
  }).catch(() => null);

  for (const [name, { mat, spec }] of seen) {
    if (!spec.map) continue;
    pending += 3;
    load(spec.map, true).then((tex) => {
      if (tex) {
        mat.map = tex;
        // The palette colour was doing the whole job before; left in, it
        // multiplies the map and darkens it.
        mat.color.set('#ffffff');
        mat.needsUpdate = true;
        console.log(`[texture] ${roomId}.${name} ← ${spec.map}`);
      }
      settle();
    });
    load(`${spec.map}_n`, false).then((tex) => {
      if (tex) {
        mat.normalMap = tex;
        mat.normalScale = new THREE.Vector2(spec.normalScale ?? 0.5, spec.normalScale ?? 0.5);
        mat.needsUpdate = true;
      }
      settle();
    });
    load(`${spec.map}_r`, false).then((tex) => {
      if (tex) {
        mat.roughnessMap = tex;
        // roughnessMap MULTIPLIES material.roughness, so a base below 1 would
        // scale the whole authored range down and flatten the variation the map
        // exists to provide.
        mat.roughness = 1;
        mat.needsUpdate = true;
      }
      settle();
    });
  }
  return seen.size;
}
